import type { ISbStoriesParams } from '@storyblok/vue';
import { reuseSsrPayloadOnServer } from '~/utils/reuseSsrPayload';

interface Options {
    /** If set to true, the country segment will not be added to the URL. */
    disableCountrySegment?: boolean;
    /** If set to true, the fetch is skipped. */
    disabled?: boolean;
}

/**
 * Fetches multiple stories from Storyblok based on the provided parameters.
 *
 * For fetching any single arbitrary story, use `useStory`. For fetching the current config, use `useConfig`. For
 * fetching the global config, use `useGlobalConfig`. For fetching datasource entries, use `useDatasourceEntries`.
 *
 * **Example:**
 * ```ts
 * import { useStories } from '~/composables/storyblok-proxy/useStories';
 *
 * const { data: newsData } = await useStories<PageNewsStoryblok>({
 *     filter_query: {
 *         component: {
 *             in: 'PageNews'
 *         }
 *     }
 * });
 * ```
 *
 * @param params - The parameters to filter the stories. Don't add the app folder or country segment to `starts_with`, they are added automatically unless disabled via options.
 * @param options - Optional settings. See {@link Options} for more details.
 * @returns A reactive object containing the fetched stories data, loading state, and error state.
 * @throws Will throw an fatal error if the fetch fails.
 */
export const useStories = <T>(params: Ref<ISbStoriesParams> | ISbStoriesParams, options?: MaybeRef<Options>) => {
    const { lang, country } = useLanguage();
    const optionsRef = toRef(options);
    const paramsRef = toRef(params);
    const _params = computed(() => {
        const params = JSON.parse(JSON.stringify(paramsRef.value));

        if (params.starts_with) {
            const segments = [];
            if (!optionsRef.value?.disableCountrySegment) segments.push(country.value);
            segments.push(...params.starts_with.split('/').filter(Boolean));
            params.starts_with = segments.join('/');
        }

        return JSON.stringify(params);
    });
    const headers = useRequestHeaders(['cookie']);
    const dataKey = computed(
        () =>
            `stories:${country.value}:${lang.value}:${_params.value}:${optionsRef.value?.disabled ? 'disabled' : 'enabled'}`
    );

    const result = useAsyncData(
        dataKey,
        (_nuxtApp, { signal }) => {
            // If the fetch is disabled, return an empty array
            if (optionsRef.value?.disabled === true) {
                return Promise.resolve([] as ExtendedISbStoryData<T>[]);
            }

            return $fetch<ExtendedISbStoryData<T>[]>(StoryblokProxyEndpoint, {
                query: {
                    type: 'stories',
                    language: lang.value,
                    params: _params.value
                },
                headers,
                signal
            });
        },
        {
            watch: [country, lang, _params, optionsRef],
            // `dedupe` only joins calls that overlap in time; `getCachedData` is what
            // makes a second caller later in the same render reuse the first response.
            dedupe: 'defer',
            getCachedData: reuseSsrPayloadOnServer
        }
    );

    watch(result.error, (error) => {
        if (!error) return;

        throw createError({
            statusCode: error.statusCode,
            message: error.message,
            fatal: true
        });
    });

    return result;
};
